<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from "vuex";
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker?worker'
import axios from 'axios'

const store = useStore();

// State
const searchQuery = ref('');
const selectedCategory = ref('all');
const showPdfPreview = ref(false);
const previewPdf = ref(null);
const pdfViewerContainer = ref(null);
const pdfLoadingError = ref(false);
const pdfLoading = ref(false);

// Filtered PDFs
const filteredPdfs = computed(() => {
  let result = [...store.state.me.user_pdfs];
  
  // Apply search filter
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(pdf => 
      pdf.name.toLowerCase().includes(query) || 
      pdf.description.toLowerCase().includes(query) ||
      pdf.category.toLowerCase().includes(query)
    );
  }
  
  // Apply category filter
  if (selectedCategory.value !== 'all') {
    result = result.filter(pdf => pdf.category === selectedCategory.value);
  }
  
  return result;
});

function loadPdfAndDownload(pdf_name) {
  loadCertification(pdf_name, true)
}

function downloadPdf() {
  // Create download link
  const link = document.createElement('a');
  link.href = previewPdf.value.base64;
  link.download = previewPdf.value.filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Show PDF preview with PDF.js
function showPdfPreviewModal(pdf_name) {
  console.log('entry showPdfPreviewModal', pdf_name)
  showPdfPreview.value = true;
  pdfLoadingError.value = false;
  pdfLoading.value = true;
  loadCertification(pdf_name, false)
}

async function renderPdfPreview() {
  console.log('entry renderPdfPreview');
  // Wait for DOM update
  console.log('vor promise')
  await new Promise(resolve => setTimeout(resolve, 100));
  console.log('nach promise')

  try {
    // Load PDF document

    console.log('previewPdf', previewPdf.value)
    const loadingTask = pdfjsLib.getDocument(previewPdf.value.base64);
    const pdfDoc = await loadingTask.promise;

    console.log('pdfDoc', pdfDoc)
    // Clear container
    if (pdfViewerContainer.value) {
      console.log('entry clearcontainer')
      pdfViewerContainer.value.innerHTML = '';

      // Render first page
      const page = await pdfDoc.getPage(1);
      const scale = 1;
      const viewport = page.getViewport({ scale });

      // Create canvas
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      // Render page
      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };
      console.log('before page.render')
      await page.render(renderContext).promise;
      console.log('after page.render')

      // Add canvas to container
      pdfViewerContainer.value.appendChild(canvas);

      // Add page info
      const pageInfo = document.createElement('div');
      pageInfo.className = 'text-center mt-3 text-secondary';
      pageInfo.innerHTML = `Seite 1 von ${pdfDoc.numPages}`;
      console.log('pageInfo', pageInfo)
      pdfViewerContainer.value.appendChild(pageInfo);
    }

  } catch (error) {
    console.error('Fehler beim Laden der PDF:', error);
    pdfLoadingError.value = true;
  } finally {
    console.log('finally')
    pdfLoading.value = false; 
  }
}

// Close PDF preview
function closePdfPreview() {
  showPdfPreview.value = false;
  previewPdf.value = null;
}

function loadCertification(name, is_download){
  axios
    .get('/api/v1/account/certifications/files/'+name+'/')
    .then(response => {
      previewPdf.value = response.data;
      console.log('pdf fertig geladen')
      if(is_download){
        downloadPdf()
      }else{
        renderPdfPreview()
      }
    })
    .catch(error => {
      console.log('error loadCertifications')
        if (error.response) {                      
            console.error(JSON.stringify(error.response.data))
        } else if (error.message) {
          console.error(JSON.stringify(error.message))
        } else {
          console.error(JSON.stringify(error))
        }
    })
}

onMounted(() => {
    
});

</script>

<template>
  <div class="container py-4">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h1 class="h3 mb-1">Meine Dokumente</h1>
            <p class="text-secondary mb-0">
              Hier finden Sie alle Ihre PDF-Dokumente
            </p>
          </div>
          <div class="d-flex align-items-center gap-2">
            <span class="badge bg-primary">{{ filteredPdfs.length }} Dokumente</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Grid View -->
    <div class="row g-4">
      <div v-for="pdf in filteredPdfs" :key="pdf.id" class="col-md-6 col-lg-4">
        <div class="card h-100 shadow-sm card-hover">
          
          <div class="card-body">
            <div class="d-flex align-items-start mb-3">
              <i class="bi bi-file-earmark-pdf text-danger me-3" style="font-size: 2rem;"></i>
              <div class="flex-grow-1">
                <h5 class="card-title mb-1">{{ pdf.pdf_name }}</h5>
              </div>
            </div>
            
            <div class="d-flex justify-content-between align-items-center">
              <div class="btn-group btn-group-sm">
                <button 
                  class="btn btn-outline-primary" 
                  @click="showPdfPreviewModal(pdf.pdf_name)"
                  title="Vorschau"
                >
                  <i class="bi bi-eye"></i>
                </button>
                <button 
                  class="btn btn-primary" 
                  @click="loadPdfAndDownload(pdf.pdf_name)"
                  title="Herunterladen"
                >
                  <i class="bi bi-download"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Empty State -->
    <div v-if="filteredPdfs.length === 0" class="text-center py-5">
      <i class="bi bi-file-earmark-pdf display-1 text-secondary mb-3"></i>
      <h2 class="h4">Keine Dokumente gefunden</h2>
      <p class="text-secondary">
        {{ searchQuery || selectedCategory !== 'all' 
           ? 'Keine Dokumente entsprechen Ihren Filterkriterien.' 
           : 'Ihnen wurden noch keine PDF-Dokumente zugewiesen.' }}
      </p>
      <button 
        v-if="searchQuery || selectedCategory !== 'all'" 
        class="btn btn-primary"
        @click="searchQuery = ''; selectedCategory = 'all'"
      >
        Filter zurücksetzen
      </button>
    </div>

    <!-- PDF Preview Modal -->
    <div v-if="showPdfPreview" class="modal-backdrop fade show"></div>
    <div v-if="showPdfPreview" class="modal d-block" tabindex="-1" @click="closePdfPreview">
      <div class="modal-dialog modal-xl modal-dialog-centered" @click.stop>
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-file-earmark-pdf text-danger me-2"></i>
              {{ previewPdf?.filename }}
            </h5>
            <button type="button" class="btn-close" @click="closePdfPreview"></button>
          </div>
          <div class="modal-body p-0">
            <div class="pdf-preview-container">
              <!-- Loading State -->
              <div v-if="pdfLoading" class="text-center py-5">
                <div class="spinner-border text-primary mb-3" role="status">
                  <span class="visually-hidden">Laden...</span>
                </div>
                <p class="text-secondary">PDF wird geladen...</p>
              </div>
              
              <!-- Error State -->
              <div v-else-if="pdfLoadingError" class="text-center py-5">
                <i class="bi bi-exclamation-triangle text-warning display-1 mb-3"></i>
                <p class="text-secondary">PDF-Vorschau konnte nicht geladen werden.</p>
                <button class="btn btn-primary" @click="showPdfPreviewModal(previewPdf.filename)">
                  Erneut versuchen
                </button>
              </div>
              
              <!-- PDF Viewer Container -->
              <div 
                ref="pdfViewerContainer" 
                class="pdf-viewer-content"
                style="min-height: 600px; display: flex; flex-direction: column; align-items: center; justify-content: center;"
              >
                <!-- PDF content will be rendered here -->
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <div class="me-auto">
              <small class="text-secondary">
                • {{ previewPdf?.size }} 
              </small>
            </div>
            <button type="button" class="btn btn-secondary" @click="closePdfPreview">Schließen</button>
            <a 
              v-if="previewPdf?.base64"
              :href="previewPdf.base64" 
              :download="previewPdf.filename"
              target="_blank" rel="noopener noreferrer"
              class="btn btn-primary"
            >
              <i class="bi bi-download me-2"></i>
              Herunterladen
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-backdrop {
  background-color: rgba(0, 0, 0, 0.5);
}

.card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.card-hover:hover {
  transform: translateY(-3px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

/* PDF Preview styling */
.pdf-preview-container {
  background-color: #f8f9fa;
  min-height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pdf-viewer-content {
  background-color: white;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  padding: 20px;
}

.pdf-viewer-content canvas {
  max-width: 100%;
  height: auto;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #dee2e6;
}

/* Table hover effect */
.table-hover tbody tr:hover {
  background-color: rgba(13, 110, 253, 0.05);
}

/* Badge hover effect */
.badge {
  transition: transform 0.2s ease;
}

.badge:hover {
  transform: scale(1.05);
}

/* Button group styling */
.btn-group-sm .btn {
  padding: 0.25rem 0.5rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .btn-group {
    width: 100%;
  }
  
  .btn-group .btn {
    flex: 1;
  }
}
</style>